<template>
  <div>
    Categories related to Unificado: {{ unificado }}
    <table class="default-table" v-if="!processing">
      <tbody>
        <tr v-if="userCanEditProducts">
          <td>
            <div class="select-box" v-if="!initialLoading">
              <select
                v-model="newCategory.categoryId"
                @change="loadSubcategories()"
                class="regular clear"
                name="category"
                id="category-select"
              >
                <option :value="null">Select Category</option>
                <option
                  v-for="category in categoriesOptions"
                  :key="category.id"
                  :value="category.id"
                  >{{ category.categoryName }}</option
                >
              </select>
            </div>
            <div class="select-box" v-else>Loading...</div>
          </td>
          <td>
            <select
              v-model="newCategory.subcategoryId"
              class="regular clear"
              name="subcategory"
              id="subcategory-select"
              v-if="!loadingSubcategory"
            >
              <option :value="null">Select Subcategory</option>
              <option
                v-for="subcategory in subCategoriesOptions"
                :key="subcategory.id"
                :value="subcategory.id"
                >{{ subcategory.subCategoryName }}</option
              >
            </select>
            <div v-else>Loading...</div>
          </td>
          <td>
            <img
              @click="addSubCategory()"
              class="base-icon"
              src="@/assets/icons/add.svg"
            />
          </td>
        </tr>
        <tr v-for="category in categories" :key="category.id">
          <td>{{ category.categoryName }}</td>
          <td>{{ category.subCategoryName }}</td>
          <td>
            <img
              v-if="userCanEditProducts"
              @click="deleteCategory(category)"
              class="base-icon"
              src="@/assets/icons/trash.svg"
              alt="Delete"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="loading-full">
      <BaseSmallSpinner />
    </div>
    <div class="ai-suggestion" v-if="userCanEditProducts && !processing">
      <button
        class="btn-dark-button"
        :disabled="aiLoading"
        @click="suggestCategory()"
      >
        {{ aiLoading ? "Asking Claude..." : "Suggest category with Claude" }}
      </button>
      <span v-if="aiError" class="ai-error">{{ aiError }}</span>
      <template v-if="aiSuggestion">
        <span class="confidence-dot" :class="aiSuggestion.confidence"></span>
        <span :title="aiSuggestion.reason">
          {{ aiSuggestion.categoryName }} &gt;
          {{ aiSuggestion.subCategoryName }}
          <span class="ai-reason" v-if="aiSuggestion.reason">
            — {{ aiSuggestion.reason }}
          </span>
        </span>
        <span v-if="suggestionAlreadyAssigned" class="ai-assigned">
          already assigned
        </span>
        <img
          v-else
          @click="applySuggestion()"
          class="base-icon"
          src="@/assets/icons/add.svg"
          alt="Add suggested category"
        />
      </template>
    </div>
  </div>
</template>
<script>
import {
  getCategoriesForPart,
  getCategories,
  getSubcategories,
  deleteCategoryFromProduct,
  addCategoryToProduct
} from "@/api/store/CategoriesService.js";
import { categorizeProducts } from "@/api/store/AiService.js";

export default {
  props: {
    unificado: {
      type: String,
      required: true
    },
    // Opcional: título/descripción/marca del producto. Sin esto Claude sólo
    // ve el unificado y la sugerencia pierde casi toda su precisión.
    part: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      categories: [],
      newCategory: {
        categoryId: null,
        subcategoryId: null
      },
      categoriesOptions: [],
      subCategoriesOptions: [],
      loading: false,
      loadingSubcategory: false,
      initialLoading: true,
      processing: false,
      aiSuggestion: null,
      aiLoading: false,
      aiError: ""
    };
  },
  computed: {
    userCanEditProducts() {
      return this.$store.getters["auth/userHasPermission"]("can_edit_products");
    },
    suggestionAlreadyAssigned() {
      if (!this.aiSuggestion) {
        return false;
      }
      return this.categories.some(
        category => category.subCategoryId === this.aiSuggestion.subcategoryId
      );
    }
  },
  mounted() {
    this.loadCategories();
  },
  methods: {
    async suggestCategory() {
      this.aiLoading = true;
      this.aiError = "";
      this.aiSuggestion = null;
      try {
        const res = await categorizeProducts([
          {
            unificado: this.unificado,
            nkrCode: this.part?.nkrCode,
            brand: this.part?.brand,
            oe: this.part?.oe,
            title: this.part?.title,
            description: this.part?.description
          }
        ]);
        const suggestion = (res.suggestions || [])[0];
        if (suggestion) {
          this.aiSuggestion = suggestion;
        } else {
          this.aiError = "Claude found no confident match for this part";
        }
      } catch (e) {
        this.aiError =
          e?.response?.data?.error || "Error requesting the suggestion";
      }
      this.aiLoading = false;
    },
    // Deja la sugerencia cargada en los selects y la asigna con el flujo normal.
    async applySuggestion() {
      this.newCategory.categoryId = this.aiSuggestion.categoryId;
      await this.loadSubcategories();
      this.newCategory.subcategoryId = this.aiSuggestion.subcategoryId;
      this.aiSuggestion = null;
      this.addSubCategory();
    },
    async loadSubcategories() {
      this.loadingSubcategory = true;
      this.subCategoriesOptions = await getSubcategories(
        this.newCategory.categoryId
      );
      this.loadingSubcategory = false;
    },
    addSubCategory() {
      this.processing = true;
      this.initialLoading = true;
      addCategoryToProduct(this.unificado, this.newCategory.subcategoryId)
        .then(res => {
          console.log(res);
          this.categories.unshift(res.subCategory);
          this.$toast.open({
            message: "Subcategory added",
            type: "success",
            position: "top-right"
          });
          this.newCategory.categoryId = null;
          this.newCategory.subcategoryId = null;
          this.initialLoading = false;
          this.subCategoriesOptions = [];
          this.processing = false;
        })
        .catch(e => {
          this.$toast.open({
            message: e?.response?.data?.message || "Error on request!",
            type: "error",
            position: "top-right"
          });
          this.initialLoading = false;
          this.processing = false;
        });
    },
    async loadCategories() {
      const p1 = getCategoriesForPart(this.unificado);
      const p2 = getCategories();
      Promise.all([p1, p2]).then(res => {
        this.categories = res[0];
        this.categoriesOptions = res[1];
        this.initialLoading = false;
      });
    },
    async loadSubcategory() {
      this.subCategoriesOptions = await getSubcategories(
        this.newCategory.categoryId
      );
    },
    deleteCategory(category) {
      if (
        confirm(
          "Please confirm you wan to delete the category " +
            category.categoryName +
            " - " +
            category.subCategoryName
        )
      ) {
        this.processing = true;
        deleteCategoryFromProduct(category.id).then(() => {
          this.$toast.open({
            message: "category deleted",
            type: "success",
            position: "top-right"
          });
          this.categories = this.categories.filter(x => x.id != category.id);
          this.processing = false;
        });
      }
    }
  }
};
</script>
<style lang="scss" scoped>
#filter-input {
  width: 100%;
  margin: 10px 0px;
  text-align: center;
}
.select-box {
  display: flex;
  flex-direction: row;
  #category-select {
    margin-right: 15px;
  }
}
.ai-suggestion {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
  button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .ai-error {
    color: #dc2626;
  }
  .ai-reason {
    color: #6b7280;
  }
  .ai-assigned {
    color: #6b7280;
    font-style: italic;
  }
}
.confidence-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  &.high {
    background-color: #16a34a;
  }
  &.medium {
    background-color: #d97706;
  }
  &.low {
    background-color: #dc2626;
  }
}
</style>
